import { Injectable } from '@angular/core';

import { CardStatus, RelatedCard, RidangoEpic, RidangoReduxAction } from '@ridango/core';
import { adult } from '@ridango/core/lib/data/adult-group.data';

import { ActionsObservable } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { TravelCardType } from '../../medium';
import { RelatedCardService } from '../related-card.service';
import { RelatedCardActions } from './related-card.actions';

@Injectable({
    providedIn: 'root'
})
export class RelatedCardEpics implements RidangoEpic {
    constructor(
        private actions: RelatedCardActions,
        private service: RelatedCardService
    ) {}

    register() {
        return [
            this.fetchAllRelatedCards,
            this.fetchCardsDiscountGroups,
            this.saveNewRelatedCard,
            this.saveEditedRelatedCard,
            this.deleteRelatedCard,
            this.blockRelatedCard,
            this.blockAndReplaceRelatedCard,
            this.unBlockRelatedCard,
            this.fetchCardStatus,
            this.saveSeutulippuCard
        ];
    }

    fetchAllRelatedCards = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.FETCH_ALL_RELATED_CARDS).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.getRelatedCards().pipe(
                    map((response: RelatedCard[]) => {
                        // transform payload to state-acceptable
                        // filtering results (removing duplicates AND only cards)
                        const cards = response.filter((r, index, self) => {
                            const selfIndex = self.findIndex((t) => t.id === r.id);
                            return index === selfIndex;
                        });
                        // normalizing the cards for state id: object
                        const normalized = response.reduce((acc, val) => {
                            return { ...acc, [val.id]: val };
                        }, {});
                        // setting up state object
                        const stateObject = {
                            all: cards,
                            allNormalized: normalized,
                            allIds: cards.map((c) => c.id),
                            typeEmailIds: cards.filter((c) => c.card_type === 'Q').map((c) => c.id),
                            typeTravelcardIds: cards
                                .filter(
                                    (c) =>
                                        c.card_type === TravelCardType.TravelCardU ||
                                        c.card_type === TravelCardType.TravelCardUTN2 ||
                                        c.card_type === TravelCardType.WalttiCard ||
                                        c.card_type === TravelCardType.BankCard
                                )
                                .map((c) => c.id),
                            typePersonalCodeIds: cards.filter((c) => c.card_type === 'C').map((c) => c.id)
                        };
                        return this.actions.fetchAllRelatedCardsSuccess(stateObject);
                    }),
                    catchError((error) => of(this.actions.fetchAllRelatedCardsFailed(error)))
                );
            })
        );

    fetchCardsDiscountGroups = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.FETCH_CARDS_DISCOUNT_GROUPS).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.fetchCardGroups(action.payload).pipe(
                    map((d: any) => {
                        const cardGroups = [adult].concat(d);
                        return this.actions.fetchCardsDiscountGroupsSuccess(cardGroups);
                    }),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.fetchCardsDiscountGroupsFailed(browserError));
                        }
                        return of(this.actions.fetchCardsDiscountGroupsFailed(error));
                    })
                );
            })
        );

    saveEditedRelatedCard = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.SAVE_EDITED_RELATED_CARD).pipe(
            switchMap((action: RidangoReduxAction) => {
                const { cardId, payload } = action.payload;
                return this.service.editRelatedCard(cardId, payload).pipe(
                    map((d: any) => this.actions.saveEditedRelatedCardSuccess(d)),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.saveEditedRelatedCardFailed(browserError));
                        }
                        return of(this.actions.saveEditedRelatedCardFailed(error));
                    })
                );
            })
        );

    saveNewRelatedCard = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.SAVE_NEW_RELATED_CARD).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.addRelatedCard(action.payload).pipe(
                    map((d: any) => this.actions.saveNewRelatedCardSuccess(d)),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.saveNewRelatedCardFailed(browserError));
                        }
                        return of(this.actions.saveNewRelatedCardFailed(error));
                    })
                );
            })
        );

    deleteRelatedCard = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.DELETE_RELATED_CARD).pipe(
            switchMap((action: RidangoReduxAction) => {
                const cardId = action.payload;
                return this.service.deleteRelatedCard(cardId).pipe(
                    map((d: any) => this.actions.deleteRelatedCardSuccess(d)),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.deleteRelatedCardFailed(browserError));
                        }
                        return of(this.actions.deleteRelatedCardFailed(error));
                    })
                );
            })
        );

    blockRelatedCard = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.BLOCK_RELATED_CARD).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.blockRelatedCard(action.payload.card_nr).pipe(
                    map((response: RelatedCard[]) => {
                        return this.actions.blockRelatedCardSuccess();
                    }),
                    catchError((error) => of(this.actions.blockRelatedCardFailed(error)))
                );
            })
        );

    blockAndReplaceRelatedCard = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.BLOCK_AND_REPLACE_RELATED_CARD).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.blockAndReplaceRelatedCard(action.payload).pipe(
                    map((response: RelatedCard[]) => {
                        return this.actions.blockRelatedCardSuccess();
                    }),
                    catchError((error) => of(this.actions.blockRelatedCardFailed(error)))
                );
            })
        );

    unBlockRelatedCard = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.UN_BLOCK_RELATED_CARD).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.unblockRelatedCard(action.payload.card_nr).pipe(
                    map((response: RelatedCard[]) => {
                        return this.actions.unBlockRelatedCardSuccess();
                    }),
                    catchError((error) => of(this.actions.unBlockRelatedCardFailed(error)))
                );
            })
        );

    fetchCardStatus = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.FETCH_CARD_STATUS).pipe(
            switchMap((action: RidangoReduxAction) => {
                const { cardNumber, cardType } = action.payload;
                return this.service.fetchCardStatus(cardNumber, cardType).pipe(
                    map((response: CardStatus) => this.actions.fetchCardStatusSuccess(response)),
                    catchError((error) => of(this.actions.fetchCardStatusFailed(error.error)))
                );
            })
        );

    saveSeutulippuCard = (action$: ActionsObservable<any>) =>
        action$.ofType(RelatedCardActions.SAVE_SEUTULIPPU_CARD).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.registerSeutulippu(action.payload).pipe(
                    map((d: any) => this.actions.saveSeutulippuCardSuccess(d)),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.saveSeutulippuCardFailed(browserError));
                        }
                        return of(this.actions.saveSeutulippuCardFailed(error));
                    })
                );
            })
        );
}
